import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'gchl_last_route';

// Routes that should NOT be restored (auth flows, one-time pages)
const SKIP_ROUTES = ['/auth/', '/printScorecard'];

// Live routes restore only within this window. Mid-round the WebView getting
// killed should put the scorekeeper back on their hole; a launch days later
// should not reopen a dead event's scoring screen.
const LIVE_ROUTE_TTL_MS = 12 * 60 * 60 * 1000;

/**
 * Persists the current route to localStorage and restores it on app reload.
 * This prevents iOS/Android from dumping users back to "/" when the WebView
 * process is killed in the background.
 *
 * Only active on native platforms (Capacitor) — browsers handle this fine.
 */
export function useRouteRestore() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRestored = useRef(false);

  // On mount: restore saved route (only once, only on native)
  useEffect(() => {
    if (hasRestored.current || !Capacitor.isNativePlatform()) return;
    hasRestored.current = true;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return;

    try {
      const { pathname, search, savedAt } = JSON.parse(saved);
      const liveExpired =
        pathname?.startsWith('/live') &&
        (!savedAt || Date.now() - savedAt > LIVE_ROUTE_TTL_MS);
      // Only restore if we're currently at the root (i.e., WebView reloaded)
      if (
        pathname &&
        pathname !== '/' &&
        pathname !== '/leaderboard' &&
        !liveExpired &&
        window.location.pathname === '/'
      ) {
        navigate(pathname + (search || ''), { replace: true });
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On every route change: save the current route
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const skip = SKIP_ROUTES.some((r) => location.pathname.startsWith(r));
    if (skip) return;

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        pathname: location.pathname,
        search: location.search,
        savedAt: Date.now(),
      })
    );
  }, [location.pathname, location.search]);
}
